/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import { useEffect } from '@web-stories-wp/react';
import { useAPI } from '../../api';
import type { FontData } from '@src/web-stories-wp/story-editor/src/elements/types';

/**
 * Internal dependencies
 */
import { useFile } from '../../file';

function useLoadFonts({
  fonts,
  setFonts,
  setFontCatalogStatus,
  retryToken,
}: {
  fonts: FontData[];
  setFonts: (fonts: FontData[]) => void;
  setFontCatalogStatus: (status: 'pending' | 'ready' | 'failed') => void;
  retryToken: number;
}) {
  const fontsLength = fonts.length;
  const {
    actions: { getFonts: getFontsFromFile },
  } = useFile();

  const {
    actions: { getFonts },
  } = useAPI();

  useEffect(() => {
    const abortController = new AbortController();

    async function loadFonts() {
      setFontCatalogStatus('pending');
      try {
        const newFonts = await getFontsFromFile();

        if (abortController.signal.aborted) {
          return;
        }

        let customFonts = await getFonts({
          signal: abortController.signal,
        });

        if (abortController.signal.aborted) {
          return;
        }

        customFonts = customFonts.sort((a, b) =>
          a.family.localeCompare(b.family)
        );

        const formattedFonts = [...customFonts, ...newFonts].map((font) => ({
          ...font,
          id: font.family,
          name: font.family,
          value: font.family,
        }));

        if (!abortController.signal.aborted) {
          setFonts(formattedFonts);
          setFontCatalogStatus('ready');
        }
      } catch (error) {
        if (!abortController.signal.aborted) {
          setFontCatalogStatus('failed');
          console.error(error);
        }
      }
    }
    if (!fontsLength) {
      loadFonts();
    }
    return () => abortController.abort();
  }, [
    fontsLength,
    setFonts,
    setFontCatalogStatus,
    getFonts,
    getFontsFromFile,
    retryToken,
  ]);
}

export default useLoadFonts;
